import { useState } from "react";
import { Link } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Globe, ArrowLeft, Mail, CheckCircle } from "lucide-react";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Use XMLHttpRequest to avoid body stream issues
  const apiRequest = (url, options) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method || 'GET', url, true);
      if (options.headers) {
        Object.keys(options.headers).forEach(key => {
          xhr.setRequestHeader(key, options.headers[key]);
        });
      }
      xhr.timeout = 10000;
      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, data });
        } catch (e) {
          resolve({ ok: false, data: { detail: 'Invalid server response' } });
        }
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.ontimeout = () => reject(new Error('Request timed out'));
      xhr.send(options.body || null);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { ok, data } = await apiRequest(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      if (ok) {
        setSubmitted(true);
        toast.success("Reset link sent! Check your email.");
      } else {
        toast.error(data.detail || "Something went wrong");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Globe className="w-10 h-10 text-slate-900" />
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Book an Interpreter</h1>
          </div>
          <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            {submitted ? "Check Your Email" : "Forgot Password"}
          </h2>
          <p className="text-slate-600 mt-2" style={{ fontFamily: 'Public Sans' }}>
            {submitted 
              ? "We've sent you a password reset link" 
              : "Enter your email to receive a reset link"}
          </p>
        </div>

        <div className="card p-8">
          {submitted ? (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <div className="space-y-2">
                <p className="text-slate-700">
                  If an account exists for <strong>{email}</strong>, you will receive an email with instructions to reset your password.
                </p>
                <p className="text-sm text-slate-500">
                  The link will expire in 1 hour.
                </p>
              </div>
              <div className="pt-4 space-y-3">
                <Button
                  onClick={() => setSubmitted(false)}
                  variant="outline"
                  className="w-full"
                  data-testid="try-another-email-btn"
                >
                  Try another email
                </Button>
                <Link to="/login" className="block">
                  <Button
                    variant="ghost"
                    className="w-full text-slate-600 hover:text-slate-900"
                    data-testid="back-to-login-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    data-testid="forgot-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11 rounded-md border-slate-200 focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <Button
                data-testid="forgot-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-md py-6 font-semibold shadow-sm hover:shadow-md transition-all"
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
            </form>
          )}

          {!submitted && (
            <div className="mt-6 text-center">
              <p className="text-slate-600" style={{ fontFamily: 'Public Sans' }}>
                Remember your password?{" "}
                <Link to="/login" className="text-slate-900 font-semibold hover:underline">
                  Log in here
                </Link>
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-slate-600 hover:text-slate-900 transition-colors">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
